import React, { createContext, useContext, useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { useLocation, Navigate } from 'react-router-dom';
import NotFoundPage from '../pages/NotFoundPage';
const { resolveSeo } = require('./registry');

const SeoContext = createContext(null);
export const useSeo = () => useContext(SeoContext);

export const SeoProvider = ({ children }) => {
  const location = useLocation();
  const [products, setProducts] = useState(() => {
    if (Array.isArray(globalThis.__SEO_PRODUCTS__)) return globalThis.__SEO_PRODUCTS__;
    try {
      const data = JSON.parse(document.getElementById('seo-catalog-bootstrap')?.textContent || 'null');
      return Array.isArray(data) ? data : null;
    } catch { return null; }
  });
  const [error, setError] = useState(null);
  const [overrides, setOverrides] = useState(null);
  const key = location.pathname + location.search;
  useEffect(() => {
    const controller = new AbortController();
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/products`, {signal: controller.signal})
      .then(response => { if (!response.ok) throw new Error('Catalogue indisponible'); return response.json(); })
      .then(setProducts).catch(err => { if (err.name !== 'AbortError') setError(err.message); });
    return () => controller.abort();
  }, []);
  const base = useMemo(() => resolveSeo(location.pathname, location.search, products), [location.pathname, location.search, products]);
  const seo = overrides?.key === key ? {...base, ...overrides.value} : base;
  useLayoutEffect(() => {
    const origin = process.env.REACT_APP_SITE_URL;
    if (!origin) throw new Error('REACT_APP_SITE_URL est requis');
    const canonical = new URL(seo.path, origin).href;
    const setMeta = (attribute, name, content) => {
      const matches = [...document.head.querySelectorAll(`meta[${attribute}="${name}"]`)];
      const tag = matches.shift() || document.createElement('meta');
      matches.forEach(node => node.remove());
      tag.setAttribute(attribute, name); tag.setAttribute('content', content); document.head.appendChild(tag);
    };
    document.title = seo.title;
    setMeta('name', 'description', seo.description);
    setMeta('name', 'robots', seo.robots);
    const canonicals = [...document.head.querySelectorAll('link[rel="canonical"]')];
    const link = canonicals.shift() || document.createElement('link');
    canonicals.forEach(node => node.remove());
    link.rel = 'canonical'; link.href = canonical; document.head.appendChild(link);
    [['title', seo.title], ['description', seo.description], ['url', canonical], ['type', seo.type || 'website'], ['image', `${origin}/favicon.jpg`]].forEach(([name, value]) => setMeta('property', `og:${name}`, value));
    [['title', seo.title], ['description', seo.description], ['url', canonical], ['image', `${origin}/favicon.jpg`]].forEach(([name, value]) => setMeta('name', `twitter:${name}`, value));
  }, [seo.title, seo.description, seo.path, seo.robots, seo.type]);
  return <SeoContext.Provider value={{seo, products, loading: products === null && !error, error, key, setOverrides}}>{children}</SeoContext.Provider>;
};

export const usePageSeo = (value) => {
  const {key, setOverrides} = useSeo();
  const serialized = JSON.stringify(value);
  useEffect(() => {
    setOverrides({key, value: JSON.parse(serialized)});
    return () => setOverrides(current => current?.key === key ? null : current);
  }, [key, serialized, setOverrides]);
};

export const SeoRouteGuard = ({children}) => {
  const {seo} = useSeo();
  const location = useLocation();
  if (seo.redirect) {
    const params = new URLSearchParams(location.search);
    if (location.pathname === '/categories') { params.delete('category'); params.delete('subcategory'); }
    const query = params.toString();
    return <Navigate to={`${seo.redirect}${query ? `?${query}` : ''}`} replace />;
  }
  return children;
};

export const useIsNotFound = () => {
  const {seo, loading} = useSeo();
  const location = useLocation();
  return seo.status === 404 && !loading && !location.pathname.startsWith('/reservation/');
};